<template>
  <div>
    <el-dialog
      :width="typeItems.length * 280 + 72 + 'px'"
      :visible="activeStep === 'campaign'"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
    >
      <div slot="title">
        <div class="el-dialog__title">{{ $t('Campaign_Dialog_Title') }}</div>
        <div class="sub-title">{{ $t('Campaign_Dialog_CampaignType') }}</div>
      </div>
      <grid
        :items="typeItems"
        :active-type="campaignType"
        @typeChanged="newType => onCampaignTypeClick(newType)"
      />
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          icon="el-icon-check"
          :disabled="campaignType !== 'inapp'"
          @click="onCreateBtnClick"
          data-qa="campaign-style-next-btn"
        >
          {{ $t('Campaign_Dialog_Button_Create') }}
        </el-button>
        <el-button
          v-if="!isTemplateCampaign"
          type="primary"
          plain
          @click="onViewDraftsBtnClick"
          data-qa="view-drafts-btn"
        >
          <span>{{ $t('Campaign_Dialog_Button_ViewDraft') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      width="auto"
      :visible="activeStep === 'trigger'"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
    >
      <div slot="title">
        <div class="el-dialog__title">{{ $t('Campaign_Dialog_Title') }}</div>
        <div class="sub-title">{{ $t('Campaign_Dialog_CampaignTrigger') }}</div>
      </div>
      <grid
        :items="triggerItems"
        :active-type="triggerType"
        @typeChanged="newType => onTriggerTypeClick(newType)"
      />
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          icon="el-icon-check"
          :disabled="triggerType === null"
          @click="onCreateBtnClick"
          data-qa="create-campaign-triggered-btn"
        >
          {{ $t('Campaign_Dialog_Button_Create') }}
        </el-button>
        <el-button
          v-if="hasMultipleTypes"
          type="primary"
          plain
          icon="el-icon-arrow-left"
          @click="onBackFromTriggerClick"
          data-qa="campaign-trigger-back-btn"
        >
          {{ $t('Campaign_Dialog_Button_Back') }}
        </el-button>
        <el-button v-else type="primary" plain @click="onViewDraftsBtnClick">
          <span>{{ $t('Campaign_Dialog_Button_ViewDraft') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :width="mxaTypeItems.length * 280 + 72 + 'px'"
      :visible="activeStep === 'mxa'"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
    >
      <template #title>
        <div class="el-dialog__title">{{ $t('Campaign_Dialog_Title') }}</div>
        <div class="sub-title">{{ $t('Campaign_Dialog_MxaType') }}</div>
      </template>
      <grid
        :items="stepNewMxaItems"
        :active-type="mxaType"
        @typeChanged="newType => onMxaTypeClick(newType)"
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button
            type="primary"
            icon="el-icon-check"
            :disabled="true"
            @click="onCreateBtnClick"
            data-qa="campaign-style-next-btn"
          >
            {{ $t('Campaign_Dialog_Button_Create') }}
          </el-button>
          <el-button
            type="primary"
            plain
            icon="el-icon-arrow-left"
            @click="onBackFromMxaTypeClick"
            data-qa="mxa-picker-back-btn"
          >
            {{ $t('Campaign_Dialog_Button_Back') }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <new-mxa-upsell-dialog
      @upsell-close="showNewMxaUpsell = false"
      :visible="showNewMxaUpsell"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { projectRoute } from '@/core/helpers'
import { $hasFeature } from '@/core/utils/VueAcl'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import Grid from '@/common/components/layout/Grid'
import NewMxaUpsellDialog from '@/modules/campaign-manage/components/NewMxaUpsellDialog'

export default {
  name: 'CreateCampaignDialog',

  components: {
    Grid,
    NewMxaUpsellDialog
  },

  props: {
    dialogVisible: {
      type: Boolean,
      required: true
    },
    isTemplateCampaign: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      campaignType: null,
      triggerType: null,
      mxaType: null,
      activeStep: null,
      showNewMxaUpsell: false,
      stepNewMxaItems: [
        {
          type: 'old',
          title: 'Campaign_Dialog_OldMxa',
          description: 'Campaign_Dialog_OldMxa_Description',
          icon: 'campaign-multistage'
        },
        {
          type: 'new',
          title: 'Campaign_Dialog_NewMxa',
          description: 'Campaign_Dialog_NewMxa_Description',
          icon: 'campaign-multistage',
          beta: true
        }
      ]
    }
  },

  methods: {
    onCampaignTypeClick(type) {
      this.campaignType = type
      this.triggerType = null

      switch (type) {
        case 'standard':
          this.activeStep = 'trigger'
          break

        case 'workflow':
          this.activeStep = 'mxa'
          break
      }
    },

    onMxaTypeClick(type) {
      if (type === 'new' && !this.isNewMxaEnabled) {
        this.showNewMxaUpsell = true
      } else {
        this.mxaType = type
        this.activeStep = 'trigger'
      }
    },

    onTriggerTypeClick(type) {
      this.triggerType = type
    },

    onBackFromMxaTypeClick() {
      this.campaignType = null
      this.activeStep = 'campaign'
    },

    onBackFromTriggerClick() {
      if (this.campaignType === 'workflow') {
        this.mxaType = null
        this.activeStep = 'mxa'
      } else {
        this.campaignType = null
        this.activeStep = 'campaign'
      }
    },

    onCreateBtnClick() {
      const resultRoute = projectRoute({
        name: this.isTemplateCampaign
          ? 'CreateTemplateCampaign'
          : 'CreateCampaign',
        query: this.queryAttributes
      })

      if (!window.xp.project) {
        window.location.assign(this.$router.resolve(resultRoute).href)
      } else {
        this.$router.push(resultRoute).catch(() => {})
        this.handleClose()
      }
    },

    onViewDraftsBtnClick() {
      this.$router
        .push(
          projectRoute({
            name: 'DraftCampaignsTab'
          })
        )
        .catch(() => {})
      this.handleClose()
    },

    handleClose() {
      this.$emit('update:dialogVisible', false)
      setTimeout(() => {
        this.campaignType = this.triggerType = this.mxaType = null
      }, 500)
      this.$parent.$data.key = new Date().getTime()
      this.activeStep = null
    }
  },

  computed: {
    ...mapGetters(['isOptimoveAvailable', 'isSalesforceAvailable']),

    queryAttributes() {
      let trigger = campaignTriggerTypes.TRIGGER_NONE
      let type = campaignTypeEnum.TYPE_NOTIFICATION

      if (this.triggerType === 'salesforce') {
        trigger = campaignTriggerTypes.TRIGGER_SALESFORCE
      } else if (this.triggerType === 'optimove') {
        trigger = campaignTriggerTypes.TRIGGER_OPTIMOVE
      } else if (this.triggerType === 'location') {
        trigger = campaignTriggerTypes.TRIGGER_LOCATION
      } else if (this.triggerType === 'event') {
        trigger = campaignTriggerTypes.TRIGGER_EVENT
      } else if (this.triggerType === 'accumulation') {
        trigger = campaignTriggerTypes.TRIGGER_ACCUMULATION
      } else if (this.triggerType === 'api') {
        trigger = campaignTriggerTypes.TRIGGER_API
      }

      if (this.campaignType === 'workflow') {
        type = campaignTypeEnum.TYPE_WORKFLOW
      } else if (this.campaignType === 'inapp') {
        type = campaignTypeEnum.TYPE_INAPP
        trigger = campaignTriggerTypes.TRIGGER_EVENT
      }

      let query = { type }
      if (trigger) {
        query.trigger = trigger
      }
      if (this.isNewMxaEnabled && this.campaignType === 'workflow') {
        query.new_mxa = this.mxaType === 'new' ? 1 : 0
      }
      return query
    },

    typeItems() {
      let items = [
        {
          type: 'standard',
          title: 'Campaign_Dialog_Type_SingleStage',
          description: 'Campaign_Dialog_Type_SingleStage_Description',
          icon: 'campaign-standard'
        }
      ]

      if ($hasFeature('maxautomation')) {
        items.push({
          type: 'workflow',
          title: 'Campaign_Dialog_Type_MultiStage',
          description: 'Campaign_Dialog_Type_MultiStage_Description',
          icon: 'campaign-multistage'
        })
      }

      if ($hasFeature('inapp') || $hasFeature('onsite')) {
        items.push({
          type: 'inapp',
          title: 'Campaign_Dialog_Type_InApp',
          description: 'Campaign_Dialog_Type_InApp_Description',
          icon: 'inapp-message'
        })
      }

      return items
    },

    mxaTypeItems() {
      return [
        {
          type: 'old',
          title: 'Campaign_Dialog_OldMxa',
          description: 'Campaign_Dialog_OldMxa_Description',
          icon: 'campaign-multistage'
        },
        {
          type: 'new',
          title: 'Campaign_Dialog_NewMxa',
          description: 'Campaign_Dialog_NewMxa_Description',
          icon: 'campaign-multistage',
          beta: true
        }
      ]
    },

    triggerItems() {
      let items = [
        {
          type: 'scheduled',
          title: 'Campaign_Dialog_Type_TimeBased',
          description: 'Campaign_Dialog_Type_TimeBased_Description',
          icon: 'stopwatch'
        },
        {
          type: 'event',
          title: 'Campaign_Dialog_Trigger_Event',
          description: 'Campaign_Dialog_Trigger_Event_Description',
          icon: 'event'
        }
      ]

      if ($hasFeature('accumulation')) {
        items.push({
          type: 'accumulation',
          title: 'Campaign_Dialog_Trigger_Accumulation',
          description: 'Campaign_Dialog_Trigger_Accumulation_Description',
          icon: 'event'
        })
      }

      if ($hasFeature('hasLocations')) {
        items.push({
          type: 'location',
          title: 'Campaign_Dialog_Trigger_Location',
          description: 'Campaign_Dialog_Trigger_Location_Description',
          icon: 'location'
        })
      }

      items.push({
        type: 'api',
        title: 'Campaign_Dialog_Trigger_Api',
        description: 'Campaign_Dialog_Trigger_Api_Description',
        icon: 'api'
      })

      if (this.campaignType !== 'workflow') {
        if (this.isOptimoveAvailable) {
          items.push({
            type: 'optimove',
            title: 'Campaign_Dialog_Type_Optimove',
            description: 'Campaign_Dialog_Type_Optimove_Description',
            icon: 'optimove'
          })
        }

        if (this.isSalesforceAvailable) {
          items.push({
            type: 'salesforce',
            title: 'Campaign_Dialog_Type_Salesforce',
            description: 'Campaign_Dialog_Type_Salesforce_Description',
            icon: 'salesforce-cloud'
          })
        }
      }

      return items
    },

    isNewMxaEnabled() {
      return $hasFeature('newMxa')
    },

    hasMultipleTypes() {
      return this.typeItems.length > 1
    }
  },

  watch: {
    dialogVisible() {
      if (this.dialogVisible) {
        if (this.typeItems.length === 1) {
          this.campaignType = this.typeItems[0].type
          this.triggerType = null
          this.activeStep = 'trigger'
        } else {
          this.activeStep = 'campaign'
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-dialog__header {
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 20px;
}

/deep/ .el-dialog__body {
  padding-bottom: 10px;
}

.title-description {
  position: absolute;
  top: 50px;
}

.sub-title {
  color: #606266;
  font-size: 14px;
  padding-top: 5px;
}

/deep/ .el-dialog__footer {
  border-top: 1px solid #ebeef5;
  padding: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
}

>>> .el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
>>> .el-dialog {
  margin: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/CreateCampaignDialog.vue